import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { Marquee } from "@/components/Marquee";
import { SelectedWork } from "@/components/SelectedWork";
import { AttentionTimeline } from "@/components/AttentionTimeline";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  services, industries, backwardsSteps, whyKronos, principles, results, tiers, faqs,
} from "@/data/site";

const manifesto = [
  { num: "01", lead: "A website is not decoration.", accent: "It's infrastructure." },
  { num: "02", lead: "Design that earns attention.", accent: "Development that earns trust." },
  { num: "03", lead: "Your website should be", accent: "your best salesperson." },
];

const TrustStrip = () => (
  <section data-testid="trust-strip-section" className="py-16 sm:py-20">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5A5E6B]">Built for ambitious businesses.</p>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 gap-px bg-white/5 sm:grid-cols-4">
        {industries.slice(0, 8).map((ind, i) => (
          <Reveal key={ind} delay={i * 0.05}>
            <div data-testid={`trust-industry-${i}`} className="flex h-full items-center bg-[#0B0C0E] px-5 py-5 text-sm text-[#9498A4] transition-colors hover:text-[#F4F4F6]">
              {ind}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="philosophy-section" className="border-t border-white/5 py-28 sm:py-40">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="philosophy-tag">Chapter 01 — Philosophy</SectionTag>
      <div className="mt-14 space-y-16 sm:space-y-20">
        {manifesto.map((m, i) => (
          <Reveal key={m.num} delay={0.05 * i}>
            <div data-testid={`manifesto-line-${m.num}`} className="grid items-baseline gap-4 sm:grid-cols-[90px_1fr] sm:gap-10">
              <span className="font-mono text-sm text-[#2563EB]">{m.num}</span>
              <h2 className="max-w-4xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-[#F4F4F6] sm:text-4xl lg:text-5xl">
                {m.lead}{" "}
                <span className="font-editorial font-medium italic text-[#2563EB]">{m.accent}</span>
              </h2>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-20 max-w-2xl text-lg font-light leading-relaxed text-[#9498A4] sm:ml-[130px]">
          Your website shapes how customers perceive your business before you ever speak to them.
          Kronos combines brand strategy, thoughtful design, clean development, and conversion-focused
          structure to create websites that don't simply look impressive — they make businesses easier
          to understand, trust, and choose.
        </p>
      </Reveal>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="services-preview-section" className="border-t border-white/5 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="services-tag">Chapter 02 — Services</SectionTag>
      <Reveal>
        <h2 className="mt-6 max-w-2xl font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Everything your website needs. Nothing it doesn't.
        </h2>
      </Reveal>
      <div className="mt-16 border-t border-white/10">
        {services.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.05}>
            <Link
              to="/services"
              data-testid={`service-row-${s.id}`}
              className="group grid items-center gap-4 border-b border-white/10 py-8 transition-colors hover:bg-white/[0.02] sm:grid-cols-[80px_1fr_1.2fr_40px] sm:gap-8"
            >
              <span className="font-mono text-sm text-[#5A5E6B]">0{i + 1}</span>
              <span className="font-display text-xl font-semibold text-[#F4F4F6] transition-colors group-hover:text-[#2563EB] sm:text-2xl">
                {s.title}
              </span>
              <span className="text-sm leading-relaxed text-[#9498A4]">{s.tagline}</span>
              <ArrowRight size={18} className="hidden text-[#5A5E6B] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#2563EB] sm:block" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Backwards = () => (
  <section data-testid="backwards-section" className="bg-[#F4F4F6] py-24 text-[#0B0C0E] sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#0B0C0E]/50">
        <span className="h-px w-8 bg-[#2563EB]" /> Chapter 05 — Approach
      </div>
      <Reveal>
        <h2 className="mt-8 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
          Most websites are built backwards.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-[#0B0C0E]/70">
          Design isn't the first step. Understanding the business is. Before choosing fonts, layouts,
          or animations, we determine who the site needs to persuade, what those visitors need to
          understand, and what action the experience should drive.
        </p>
      </Reveal>
      <div className="mt-16 grid gap-px bg-[#0B0C0E]/10 sm:grid-cols-2 lg:grid-cols-5">
        {backwardsSteps.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.07}>
            <div data-testid={`backwards-step-${s.num}`} className="flex h-full flex-col bg-[#F4F4F6] p-6">
              <span className="font-mono text-sm text-[#2563EB]">{s.num}</span>
              <p className="mt-4 font-display text-lg font-semibold">{s.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#0B0C0E]/60">{s.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Results = () => (
  <section data-testid="results-section" className="border-t border-white/5 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="results-tag">Chapter 06 — Outcomes</SectionTag>
      <Reveal>
        <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Design with a business objective.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {results.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.07}>
            <div data-testid={`result-card-${i}`} className="h-full bg-[#0B0C0E] p-8 transition-colors hover:bg-[#121418]">
              <span className="block h-px w-8 bg-[#2563EB]" />
              <p className="mt-6 font-display text-lg font-semibold text-[#F4F4F6]">{r.title}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#9498A4]">{r.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyKronos = () => (
  <section data-testid="why-kronos-section" className="border-t border-white/5 bg-[#08090B] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="why-kronos-tag">Chapter 07 — Why Kronos</SectionTag>
      <Reveal>
        <h2 className="mt-6 max-w-2xl font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Designed differently because we think differently.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {whyKronos.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.06}>
            <div data-testid={`why-kronos-item-${i}`}>
              <p className="font-mono text-xs text-[#2563EB]">0{i + 1}</p>
              <p className="mt-3 font-display text-lg font-semibold text-[#F4F4F6]">{w.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#9498A4]">{w.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Principles = () => (
  <section data-testid="principles-section" className="border-t border-white/5 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="principles-tag">Chapter 08 — Standards</SectionTag>
      <Reveal>
        <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Our standard for good web design.
        </h2>
      </Reveal>
      <div className="mt-16 border-t border-white/10">
        {principles.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.04}>
            <div data-testid={`principle-row-${i}`} className="grid gap-2 border-b border-white/10 py-7 sm:grid-cols-[1fr_1.4fr] sm:gap-8">
              <p className="font-display text-lg font-semibold text-[#F4F4F6] sm:text-xl">{p.title}</p>
              <p className="text-sm leading-relaxed text-[#9498A4] sm:text-base">{p.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section data-testid="industries-section" className="border-t border-white/5 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="industries-tag">Chapter 09 — Industries</SectionTag>
      <Reveal>
        <h2 className="mt-6 max-w-2xl font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Designed around your industry. Not trapped by it.
        </h2>
      </Reveal>
      <div className="mt-14 flex flex-wrap gap-3">
        {industries.map((ind, i) => (
          <Reveal key={ind} delay={i * 0.04}>
            <span
              data-testid={`industry-chip-${i}`}
              className="rounded-full border border-white/10 px-5 py-2.5 text-sm text-[#9498A4] transition-colors hover:border-[#2563EB]/50 hover:text-[#F4F4F6]"
            >
              {ind}
            </span>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section data-testid="pricing-section" className="border-t border-white/5 bg-[#08090B] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="pricing-tag">Chapter 10 — Engagements</SectionTag>
      <Reveal>
        <h2 className="mt-6 max-w-2xl font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Every project is scoped around the business.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-px bg-white/10 lg:grid-cols-3">
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <div data-testid={`pricing-tier-${t.name.toLowerCase()}`} className="flex h-full flex-col bg-[#0B0C0E] p-8 transition-colors hover:bg-[#121418] sm:p-10">
              <p className="font-display text-xl font-semibold text-[#F4F4F6]">{t.name}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#9498A4]">{t.description}</p>
              <ul className="mt-8 flex-1 space-y-3 border-t border-white/10 pt-8">
                {t.items.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-[#9498A4]">
                    <span className="h-1 w-1 rounded-full bg-[#2563EB]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className="link-underline mt-10 inline-flex items-center gap-2 text-sm font-medium text-[#F4F4F6]"
              >
                Request a Project Estimate <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="faq-section" className="border-t border-white/5 py-24 sm:py-32">
    <div className="mx-auto max-w-4xl px-6 sm:px-8 lg:px-12">
      <SectionTag testId="faq-tag">Chapter 11 — FAQ</SectionTag>
      <Reveal>
        <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
          Questions, answered plainly.
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-14">
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="text-left font-display text-base font-medium text-[#F4F4F6] hover:text-[#2563EB] hover:no-underline sm:text-lg"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm leading-relaxed text-[#9498A4] sm:text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const FinalCta = () => (
  <section data-testid="final-cta-section" className="relative flex min-h-[90vh] items-center overflow-hidden border-t border-white/5">
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2563EB] opacity-[0.08] blur-[140px]" />
    <div className="noise-overlay pointer-events-none absolute inset-0" />
    <div className="relative mx-auto max-w-7xl px-6 py-28 sm:px-8 lg:px-12">
      <Reveal>
        <h2 className="max-w-4xl font-display text-3xl font-bold leading-[1.12] tracking-tight text-[#F4F4F6] sm:text-5xl">
          Your website is often your first impression.{" "}
          <span className="text-[#2563EB]">Make it count.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-[#9498A4]">
          Build a digital presence that makes your business look as good as the work behind it.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="final-cta-start-project-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#1D4ED8]"
          >
            Start a Project
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/work"
            data-testid="final-cta-view-work-button"
            className="inline-flex items-center rounded-full border border-white/15 px-8 py-4 text-sm font-medium text-[#F4F4F6] transition-colors duration-300 hover:border-[#2563EB]/60 hover:bg-white/5"
          >
            View Selected Work
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Seo
        title="Kronos — Web Design & Development Studio"
        siteName="Kronos"
        description="Kronos designs and develops high-performance websites for businesses that care about how they look, how they communicate, and how they convert."
        image="/og.jpeg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "Kronos",
          description:
            "Boutique web design and development studio building strategic, high-performance websites for ambitious businesses.",
        }}
      />
      <Hero />
      <Marquee
        testId="hero-marquee"
        items={["Strategy", "Design", "Development", "Optimization", "Branding", "Conversion"]}
      />
      <TrustStrip />
      <Manifesto />
      <ServicesPreview />
      <SelectedWork />
      <AttentionTimeline />
      <Backwards />
      <BeforeAfter />
      <Results />
      <WhyKronos />
      <Principles />
      <Industries />
      <Pricing />
      <Faq />
      <FinalCta />
    </main>
  );
}
